import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, SearchCheck } from "lucide-react";
import { toast } from "sonner";
import { useCreateSubscriber, useLookupAcquirer } from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input, Select } from "@/components/ui/Input";
import { LocationPicker } from "@/components/ui/LocationPicker";
import { PersonNameFields, suggestPersonName } from "@/components/PersonNameFields";
import type { CreateSubscriberInput, DocumentType, PersonType } from "@/types";

const DEFAULT: CreateSubscriberInput = {
  type: "juridica",
  name: "",
  firstName: "",
  secondName: "",
  firstSurname: "",
  secondSurname: "",
  documentType: "NIT",
  documentNumber: "",
  verificationDigit: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  country: "CO",
  department: "",
};

export function SubscribersCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateSubscriberInput>(DEFAULT);
  const [dianHint, setDianHint] = useState<string | null>(null);
  const create = useCreateSubscriber();
  const lookup = useLookupAcquirer();

  async function onLookup() {
    if (!form.documentNumber.trim()) {
      toast.error("Enter a document number first");
      return;
    }
    try {
      const res = await lookup.mutateAsync({
        documentType: form.documentType,
        documentNumber: form.documentNumber.trim(),
      });
      if (!res.found) {
        toast.warning(
          `DIAN: ${res.message ?? "No acquirer found for this document"}`
        );
        return;
      }
      setDianHint(res.name ?? null);
      setForm((f) => {
        const next = { ...f, email: res.email ?? f.email };
        if (f.type === "juridica") {
          next.name = res.name ?? f.name;
        }
        return next;
      });
      toast.success(
        `DIAN match: ${res.name}${res.email ? ` · ${res.email}` : ""}`
      );
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(`[${err.code}] ${err.message}`);
      } else {
        toast.error("Lookup failed");
      }
    }
  }

  function set<K extends keyof CreateSubscriberInput>(key: K, value: CreateSubscriberInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const isJuridica = form.type === "juridica";

  function onTypeChange(type: PersonType) {
    setForm((f) => ({
      ...f,
      type,
      documentType: type === "juridica" ? "NIT" : "CC",
      verificationDigit: type === "juridica" ? f.verificationDigit : "",
    }));
    setDianHint(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: CreateSubscriberInput = isJuridica
      ? {
          type: "juridica",
          name: form.name,
          documentType: form.documentType,
          documentNumber: form.documentNumber,
          verificationDigit: form.verificationDigit?.trim() || undefined,
          email: form.email,
          phone: form.phone?.trim() || undefined,
          address: form.address,
          city: form.city,
          state: form.state,
          country: form.country?.trim() || "CO",
          department: form.department?.trim() || undefined,
        }
      : {
          type: "natural",
          firstName: form.firstName,
          secondName: form.secondName?.trim() || null,
          firstSurname: form.firstSurname,
          secondSurname: form.secondSurname?.trim() || null,
          documentType: form.documentType,
          documentNumber: form.documentNumber,
          email: form.email,
          phone: form.phone?.trim() || undefined,
          address: form.address,
          city: form.city,
          state: form.state,
          country: form.country?.trim() || "CO",
        };
    try {
      const res = await create.mutateAsync(payload);
      toast.success("Subscriber created");
      navigate(`/subscribers/${res.id}`);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to create subscriber");
    }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold text-slate-900">New subscriber</h1>
        <p className="text-sm text-slate-500 mt-1">
          Register a juridica (company) or natural (person) subscriber.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Identity</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Person type" required>
              <Select
                value={form.type}
                onChange={(e) => onTypeChange(e.target.value as PersonType)}
              >
                <option value="juridica">Juridica (company)</option>
                <option value="natural">Natural (person)</option>
              </Select>
            </Field>

            {isJuridica ? (
              <Field label="Business name" required>
                <Input
                  value={form.name ?? ""}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Acme S.A.S"
                  required
                  maxLength={300}
                />
              </Field>
            ) : (
              <>
                <div />
                <PersonNameFields
                  value={{
                    firstName: form.firstName ?? "",
                    secondName: form.secondName ?? "",
                    firstSurname: form.firstSurname ?? "",
                    secondSurname: form.secondSurname ?? "",
                  }}
                  onChange={(next) =>
                    setForm((f) => ({
                      ...f,
                      firstName: next.firstName,
                      secondName: next.secondName,
                      firstSurname: next.firstSurname,
                      secondSurname: next.secondSurname,
                    }))
                  }
                  hint={dianHint}
                />
                {dianHint ? (
                  <div className="md:col-span-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const suggested = suggestPersonName(dianHint);
                        setForm((f) => ({ ...f, ...suggested }));
                      }}
                    >
                      Suggest split from DIAN name
                    </Button>
                  </div>
                ) : null}
              </>
            )}

            <Field label="Document type" required>
              <Select
                value={form.documentType}
                onChange={(e) => set("documentType", e.target.value as DocumentType)}
              >
                {isJuridica ? (
                  <option value="NIT">NIT</option>
                ) : (
                  <>
                    <option value="CC">CC</option>
                    <option value="CE">CE</option>
                    <option value="PASAPORTE">Pasaporte</option>
                  </>
                )}
              </Select>
            </Field>

            <Field
              label="Document number"
              required
              hint="Click Lookup to auto-fill from DIAN"
            >
              <div className="flex gap-2">
                <Input
                  value={form.documentNumber}
                  onChange={(e) => set("documentNumber", e.target.value)}
                  required
                  maxLength={20}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={onLookup}
                  loading={lookup.isPending}
                  disabled={!form.documentNumber.trim()}
                  aria-label="Lookup in DIAN"
                >
                  <SearchCheck className="h-4 w-4" /> Lookup
                </Button>
              </div>
            </Field>

            {isJuridica && (
              <Field label="Verification digit" hint="Single digit for NIT (0–9)">
                <Input
                  value={form.verificationDigit ?? ""}
                  onChange={(e) => set("verificationDigit", e.target.value)}
                  maxLength={1}
                />
              </Field>
            )}
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle>Contact</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email" required>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                maxLength={256}
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone ?? ""}
                onChange={(e) => set("phone", e.target.value)}
                maxLength={30}
              />
            </Field>
            <Field label="Address" required className="md:col-span-2">
              <Input
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                required
                maxLength={300}
              />
            </Field>
            <div className="md:col-span-2">
              <LocationPicker
                departmentCode={form.state || null}
                municipalityCode={form.city || null}
                onDepartmentChange={(code) => set("state", code ?? "")}
                onMunicipalityChange={(code) => set("city", code ?? "")}
                required
              />
            </div>
            <Field label="Country" hint="Always CO in this system" className="md:col-span-2">
              <Input value="CO" readOnly disabled />
            </Field>
            {isJuridica && (
              <Field label="Area / department" hint="Optional company department" className="md:col-span-2">
                <Input
                  value={form.department ?? ""}
                  onChange={(e) => set("department", e.target.value)}
                  maxLength={200}
                />
              </Field>
            )}
          </CardContent>
          <CardFooter>
            <Button type="button" variant="outline" onClick={() => navigate("/subscribers")}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create subscriber
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
